import { shareCopy } from 'src/app/shared/share/copy';
import { shareEmail } from 'src/app/shared/share/email';
import { shareFacebook } from 'src/app/shared/share/facebook';
import { shareFacebookMessenger } from 'src/app/shared/share/facebookMessenger';
import { shareFlyer } from 'src/app/shared/share/flyer';
import { shareInstagram } from 'src/app/shared/share/instagram';
import { shareInstagramStories } from 'src/app/shared/share/instagramStories';
import { shareLine } from 'src/app/shared/share/line';
import { shareNative } from 'src/app/shared/share/mobileNativeShare';
import { shareNextdoor } from 'src/app/shared/share/nextdoor';
import { shareQRCode } from 'src/app/shared/share/qrcode';
import { shareReddit } from 'src/app/shared/share/reddit';
import { shareSms } from 'src/app/shared/share/sms';
import { shareTiktok } from 'src/app/shared/share/tiktok';
import { shareTwitter } from 'src/app/shared/share/twitter';
import { shareVk } from 'src/app/shared/share/vk';
import { shareWhatsapp } from 'src/app/shared/share/whatsapp';

import type { ShareChannel, ShareConfig } from '../../shared/types';
import type { ShareActionContext } from '../actionContext';

type ShareActionOptions = {
	url: string;
	content: Readonly<{
		title: string;
		body: string;
		image: string;
	}>;
	trackingEventName: string;
	trackingData: Readonly<Record<string, boolean | string | number | undefined>>;
	config: ShareConfig;
};

type ShareAction = (options: ShareActionOptions, concontent: ShareActionContext) => Promise<boolean>;

const SHARE_ACTIONS: Readonly<Record<ShareChannel, ShareAction>> = {
	facebook: async ({ url, trackingData, trackingEventName, config: { facebook } }, { utilityContext, track }) =>
		shareFacebook(
			url,
			{
				trackingData,
				trackingEvents: {
					success: trackingEventName,
					intent: `${trackingEventName}_intent`,
					cancel: `${trackingEventName}_cancel`,
					failure: `${trackingEventName}_failure`,
				},
				mode: facebook?.mode,
			},
			utilityContext,
			track,
		),
	facebookMessenger: async ({ url, trackingData, trackingEventName }, { utilityContext, track }) => {
		shareFacebookMessenger(
			url,
			{
				trackingData,
				trackingEvents: {
					success: trackingEventName,
				},
			},
			utilityContext,
			track,
		);
		return Promise.resolve(true);
	},
	twitter: async (
		{ url, content: { title }, trackingData, trackingEventName },
		{ track, utilityContext, countryCode, locale },
	) => {
		shareTwitter({
			url,
			comment: title,
			options: { trackingData, trackingEvents: { success: trackingEventName }, countryCode, locale },
			track,
			utilityContext,
		});
		return Promise.resolve(true);
	},
	whatsapp: async ({ url, content: { title }, trackingData, trackingEventName }, { track }) => {
		shareWhatsapp(
			title,
			url,
			{
				trackingData,
				trackingEvents: {
					success: trackingEventName,
				},
			},
			track,
		);
		return Promise.resolve(true);
	},
	reddit: async ({ url, content: { title }, trackingData, trackingEventName }, { utilityContext, track }) => {
		await shareReddit(
			title,
			url,
			{
				trackingData,
				trackingEvents: {
					success: trackingEventName,
				},
			},
			track,
			utilityContext.i18n,
		);
		return Promise.resolve(true);
	},
	vk: async ({ url, content: { title }, trackingData, trackingEventName }, { track }) => {
		shareVk(
			url,
			title,
			{
				trackingData,
				trackingEvents: {
					success: trackingEventName,
				},
			},
			track,
		);
		return Promise.resolve(true);
	},
	line: async ({ url, content: { title }, trackingData, trackingEventName }, { track }) => {
		shareLine(
			url,
			title,
			{
				trackingData,
				trackingEvents: {
					success: trackingEventName,
				},
			},
			track,
		);
		return Promise.resolve(true);
	},
	email: async ({ content: { title, body }, trackingData, trackingEventName }, { track }) => {
		shareEmail(
			title,
			body,
			{
				trackingData,
				trackingEvents: {
					success: trackingEventName,
				},
			},
			track,
		);
		return Promise.resolve(true);
	},
	sms: async ({ url, content: { title }, trackingData, trackingEventName }, { utilityContext, track }) => {
		shareSms(
			url,
			title,
			{
				trackingData,
				trackingEvents: {
					success: trackingEventName,
				},
			},
			utilityContext,
			track,
		);
		return Promise.resolve(true);
	},
	instagram: async (
		{ url, trackingData, trackingEventName, config: { isSunriseEnabled } },
		{ track, utilityContext, instagram: { openEducationModal } },
	) =>
		shareInstagram(
			url,
			{
				trackingData,
				trackingEvents: {
					success: trackingEventName,
					intent: 'instagram_share_story_modal_view',
					continue: 'instagram_share_story_modal_continue',
					cancel: 'instagram_share_story_modal_close',
				},
				openEducationModal: () => openEducationModal({ isSunriseEnabled: !!isSunriseEnabled }),
			},
			track,
			utilityContext.i18n,
		),
	instagramStories: async (
		{ url, content: { image }, trackingData, trackingEventName },
		{ track, utilityContext, instagramStories: { openEducationModal } },
	) =>
		shareInstagramStories(
			{ url, image },
			{
				trackingData,
				trackingEvents: {
					success: trackingEventName,
					intent: 'instagram_stories_share_story_modal_view',
					continue: 'instagram_stories_share_story_modal_continue',
					cancel: 'instagram_stories_share_story_modal_close',
					failure: `${trackingEventName}_failure`,
				},
				openStoriesEducationModal: ({ onContinueAction, shareVisualPreview }) =>
					openEducationModal({ onContinueAction, shareVisualPreview }),
			},
			track,
			utilityContext,
		),
	tiktok: async (
		{ url, content: { image }, trackingData, trackingEventName, config: { isSunriseEnabled } },
		{ track, utilityContext, tiktok: { openEducationModal } },
	) =>
		shareTiktok(
			{ url, image },
			{
				trackingData,
				trackingEvents: {
					success: trackingEventName,
					intent: 'tiktok_share_modal_view',
					continue: 'tiktok_share_modal_continue',
					cancel: 'tiktok_share_modal_close',
					failure: `${trackingEventName}_failure`,
				},
				openTiktokEducationModal: ({ onContinueAction }) =>
					openEducationModal({ isSunriseEnabled: !!isSunriseEnabled, onContinueAction }),
			},
			track,
			utilityContext,
		),
	nextdoor: async ({ url, content: { title }, trackingData, trackingEventName }, { track }) => {
		shareNextdoor(
			url,
			title,
			{
				trackingData,
				trackingEvents: {
					success: trackingEventName,
				},
			},
			track,
		);
		return Promise.resolve(true);
	},
	copy: async ({ url, trackingData, trackingEventName }, { utilityContext, track }) =>
		shareCopy(
			url,
			{
				trackingData,
				trackingEvents: {
					success: trackingEventName,
					failure: `${trackingEventName}_failure`,
				},
			},
			track,
			utilityContext.i18n,
		),
	mobileNativeShare: async ({ url, trackingData, trackingEventName }, { track }) =>
		shareNative(
			url,
			{
				trackingData,
				trackingEvents: {
					success: trackingEventName,
					failure: `${trackingEventName}_failure`,
				},
			},
			track,
		),
	qrcode: async ({ trackingData, trackingEventName }, { track }) => {
		shareQRCode({ trackingData, trackingEvents: { success: trackingEventName } }, track);
		return Promise.resolve(true);
	},
	flyer: async ({ trackingData, trackingEventName }, { track }) => {
		shareFlyer({ trackingData, trackingEvents: { success: trackingEventName } }, track);
		return Promise.resolve(true);
	},
};

export function getShareAction(channel: ShareChannel): ShareAction {
	return SHARE_ACTIONS[channel];
}
